import { useEffect, type CSSProperties } from 'react';
import { useAtomValue } from 'jotai';
import { userAtom } from '@/global-store';
import { useFetchOrganisations } from '@http/queries';
import type { OrganizationData } from '@eye-able/types';

/**
 * White-label branding for a single customer (one org needs its own colours +
 * logo). The branded surfaces are few and known (header background, the logo,
 * and the active nav item), so we expose the parsed config and let those
 * components apply it inline — the brand colour overrides the Tailwind class
 * only when a config value exists. No global CSS / MUI-theme override.
 */
export interface OrgBranding {
  /** Primary brand colour: header + active nav-item background. */
  brandColor: string;
  /** Hover/accent shade for brand surfaces; also the header bottom border. */
  brandHoverColor: string;
  /** Text/icon colour rendered on top of `brandColor`. Defaults to white. */
  brandTextColor: string;
  customLogoUrl?: string;
}

type BrandingSource = Partial<Pick<OrganizationData, 'settings'>>;

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Branding is persisted only inside the `settings` JSON (there are no dedicated
 * columns), so we read it straight from there. Note: GET strips `settings` for
 * non-admins, so branding currently only reaches org admins. Returns `null`
 * unless a `brandColor` is set.
 *
 * Values are applied as inline `style`, so invalid ones are harmlessly ignored
 * by the browser — no need to validate/escape them. `brandColor` only gates
 * branding so we never end up with brand text on a non-branded background.
 */
function getOrgBranding(org: BrandingSource | undefined): OrgBranding | null {
  const settings =
    org?.settings && typeof org.settings === 'object'
      ? (org.settings as Record<string, unknown>)
      : undefined;
  if (!settings) return null;

  const brandColor = str(settings.brandColor);
  if (!brandColor) return null;

  return {
    brandColor,
    brandHoverColor: str(settings.brandHoverColor) ?? brandColor,
    brandTextColor: str(settings.brandTextColor) ?? '#fff',
    customLogoUrl: str(settings.customLogoUrl),
  };
}

/** Props spread onto a header ghost button so it follows the brand colours. */
export interface BrandButtonProps {
  className?: string;
  style?: CSSProperties;
}

/**
 * Styling for the header's ghost buttons: brand text colour, plus a brand
 * hover background that overrides the variant's `eye:hover:bg-grey-20` (inline
 * style can't express `:hover`, so we feed the colour through a CSS variable
 * and an important hover utility). Returns `{}` when not branded.
 */
export function getBrandButtonProps(
  branding: OrgBranding | null,
): BrandButtonProps {
  if (!branding) return {};
  return {
    className: 'eye:hover:bg-[var(--brand-hover)]!',
    style: {
      color: branding.brandTextColor,
      '--brand-hover': branding.brandHoverColor,
    } as CSSProperties,
  };
}

/**
 * Returns the current org's white-label branding, or `null` when none is
 * configured. Safe to call from multiple components: `useFetchOrganisations`
 * is react-query-cached, so the underlying request is shared/deduped.
 */
export function useOrgBranding(): OrgBranding | null {
  const user = useAtomValue(userAtom);
  // `useFetchOrganisations` (untyped JS) expects a string id; matches the cast
  // used by other TS callers (e.g. pages/organisation/Organisation.tsx).
  const { data } = useFetchOrganisations(
    user?.primaryOrganisation as unknown as string,
    { enabled: !!user },
  );

  // The endpoint always returns an array, even when filtered by id.
  const org: BrandingSource | undefined = Array.isArray(data) ? data[0] : data;
  return getOrgBranding(org);
}

/**
 * Re-colours the shared `yellow` Button variant (used ~50× across the app,
 * including inside portalled dialogs) to the brand colour. The variant reads
 * `--brand-button-*`; setting those on the document root is the one place that
 * reaches every instance, portals included. No-op / cleaned up when unbranded.
 * Mount once high in the tree (e.g. `BaseRoute`).
 */
export function useApplyBrandButtonVars(): void {
  const branding = useOrgBranding();
  const { brandColor, brandHoverColor, brandTextColor } = branding ?? {};

  useEffect(() => {
    const root = document.documentElement;
    if (!brandColor) return;

    root.style.setProperty('--brand-button-bg', brandColor);
    root.style.setProperty(
      '--brand-button-hover',
      brandHoverColor ?? brandColor,
    );
    root.style.setProperty('--brand-button-text', brandTextColor ?? '#fff');

    return () => {
      root.style.removeProperty('--brand-button-bg');
      root.style.removeProperty('--brand-button-hover');
      root.style.removeProperty('--brand-button-text');
    };
  }, [brandColor, brandHoverColor, brandTextColor]);
}
